import React from 'react';

/**
 * Static "preview" of the Vector Results KPI card grid shown to non-Pro
 * viewers. Renders the same card classes as the real grid (so layout and
 * styles stay identical) but with hard-coded example values — never the real
 * strategy's numbers. The cards are uniformly blurred via CSS in
 * StrategyDetailsView.css. Every strategy tab looks identical when locked.
 */
function StrategyStatsLockedPreview() {
  // Hard-coded example values. Chosen to look like a plausible-but-generic
  // strategy: positive net total, mid-60s win rate, ranges that fit the card
  // layouts on desktop and mobile.
  const PREVIEW = {
    bestDayValue: '$1,250',
    bestDayLabel: 'Wed',
    bestDayDate: '03.12.2025',
    worstDayValue: '-$680',
    worstDayLabel: 'Thu',
    worstDayDate: '06.05.2025',
    bestMonthValue: '$4,500',
    bestMonthLabel: 'March 2025',
    worstMonthValue: '-$1,820',
    worstMonthLabel: 'July 2025',
    winningWeeks: 28,
    losingWeeks: 12,
    netTotal: '$18,400',
    winsSum: '$26,200',
    lossesSum: '-$7,800',
    totalWinRate: '64.3',
    totalWinningDays: 135,
    totalDays: 210,
    weekdayWinRates: { Mon: 68.5, Tue: 71.0, Wed: 62.4, Thu: 58.8, Fri: 73.2 },
  };

  return (
    <>
      <div className="strategy-summary-cards strategy-summary-cards-top">
        <div className="strategy-summary-card card-best-day">
          <div className="strategy-summary-label">Best / Worst Day</div>
          <div className="strategy-summary-value stacked-values">
            <div className="stacked-line positive-inline">{PREVIEW.bestDayValue}</div>
            <div className="stacked-line stacked-line-worst negative">{PREVIEW.worstDayValue}</div>
          </div>
          <div className="strategy-summary-meta stacked-meta">
            <div className="positive-inline">
              Best: {PREVIEW.bestDayLabel} ({PREVIEW.bestDayDate})
            </div>
            <div className="negative">
              Worst: {PREVIEW.worstDayLabel} ({PREVIEW.worstDayDate})
            </div>
          </div>
        </div>

        <div className="strategy-summary-card card-best-month">
          <div className="strategy-summary-label">Best / Worst Month</div>
          <div className="strategy-summary-value stacked-values">
            <div className="stacked-line positive-inline">{PREVIEW.bestMonthValue}</div>
            <div className="stacked-line stacked-line-worst negative">{PREVIEW.worstMonthValue}</div>
          </div>
          <div className="strategy-summary-meta stacked-meta">
            <div className="positive-inline">Best: {PREVIEW.bestMonthLabel}</div>
            <div className="negative">Worst: {PREVIEW.worstMonthLabel}</div>
          </div>
        </div>

        <div className="strategy-summary-card card-winning-weeks">
          <div className="strategy-summary-label">Winning / Losing Weeks</div>
          <div className="strategy-summary-value stacked-values">
            <div className="stacked-line positive">{PREVIEW.winningWeeks}</div>
            <div className="stacked-line stacked-line-worst negative-inline">{` / ${PREVIEW.losingWeeks}`}</div>
          </div>
          <div className="strategy-summary-meta">Totals &gt; 0 / Totals &lt; 0</div>
        </div>

        <div className="strategy-summary-card card-net-total">
          <div className="strategy-summary-label">Net Total</div>
          <div className="strategy-summary-value positive">{PREVIEW.netTotal}</div>
          <div className="strategy-summary-meta">
            <span className="positive-inline">Wins: {PREVIEW.winsSum}</span>
            <br />
            <span className="negative-inline">Losses: {PREVIEW.lossesSum}</span>
          </div>
        </div>

        <div className="strategy-summary-card card-total-win-rate strategy-summary-card-total-win-rate">
          <div className="strategy-summary-label">Total Win Rate</div>
          <div className="strategy-summary-value positive">{PREVIEW.totalWinRate}%</div>
          <div className="strategy-summary-meta">{`${PREVIEW.totalWinningDays} / ${PREVIEW.totalDays} days`}</div>
        </div>
      </div>

      <div className="strategy-summary-cards strategy-summary-cards-weekday">
        {[
          { key: 'Mon', label: 'Monday' },
          { key: 'Tue', label: 'Tuesday' },
          { key: 'Wed', label: 'Wednesday' },
          { key: 'Thu', label: 'Thursday' },
          { key: 'Fri', label: 'Friday' },
        ].map(({ key, label }) => {
          const pct = PREVIEW.weekdayWinRates[key];
          return (
            <div key={key} className="strategy-summary-card">
              <div className="strategy-summary-label">{label} Win Rate</div>
              <div className={`strategy-summary-value ${pct >= 50 ? 'positive' : 'negative'}`}>
                {pct.toFixed(1)}%
              </div>
              <div className="strategy-summary-meta">Wins / total days with data</div>
            </div>
          );
        })}
        <div className="strategy-summary-card card-total-win-rate-mobile strategy-summary-card-total-win-rate">
          <div className="strategy-summary-label">Total Win Rate</div>
          <div className="strategy-summary-value positive">{PREVIEW.totalWinRate}%</div>
          <div className="strategy-summary-meta">{`${PREVIEW.totalWinningDays} / ${PREVIEW.totalDays} days`}</div>
        </div>
      </div>
    </>
  );
}

export default StrategyStatsLockedPreview;
